// src/components/OptimizedFileUpload.js - Shadcn Button 적용 버전
import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";

const OptimizedFileUpload = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [storageInfo, setStorageInfo] = useState({ used: 0, total: 0, files: 0 });

  // 최대 파일 크기 (2MB)
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const STORAGE_KEY = 'uploadedPDFsV2';

  useEffect(() => {
    updateStorageInfo();
  }, []);

  // 저장소 정보 업데이트
  const updateStorageInfo = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const files = stored ? JSON.parse(stored) : [];
      const usedBytes = new Blob([stored || '']).size;
      const usedMB = (usedBytes / 1024 / 1024).toFixed(2);
      
      setStorageInfo({
        used: usedMB,
        total: 5, // 대략적인 LocalStorage 제한
        files: files.length
      });
    } catch (error) {
      console.error('저장소 정보 업데이트 실패:', error);
    }
  };

  // 파일 크기 검증
  const validateFile = (file) => {
    if (!file) return { valid: false, message: '파일을 선택해주세요.' };
    if (file.type !== 'application/pdf') {
      return { valid: false, message: 'PDF 파일만 업로드 가능합니다.' };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        message: `파일 크기가 너무 큽니다. 최대 ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB까지 업로드 가능합니다.` 
      };
    }
    return { valid: true };
  };

  // 저장소 여유 공간 확인
  const checkStorageSpace = (file) => {
    const estimatedSize = file.size * 1.4; // Base64 인코딩 후 예상 크기
    const currentUsed = parseFloat(storageInfo.used);
    const estimatedTotal = currentUsed + (estimatedSize / 1024 / 1024);
    
    if (estimatedTotal > 4.5) { // 안전 마진 포함
      return {
        hasSpace: false,
        message: `저장 공간이 부족합니다. 현재 사용량: ${currentUsed}MB, 예상 사용량: ${estimatedTotal.toFixed(2)}MB`
      };
    }
    return { hasSpace: true };
  };

  // 가장 오래된 파일 삭제
  const deleteOldestFile = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const files = stored ? JSON.parse(stored) : [];
      
      if (files.length > 0) {
        // 가장 오래된 파일 제거
        files.pop();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(files));
        updateStorageInfo();
        return true;
      }
      return false;
    } catch (error) {
      console.error('파일 삭제 실패:', error);
      return false;
    }
  };

  // 모든 파일 삭제
  const clearAllFiles = () => {
    if (window.confirm('저장된 모든 파일을 삭제하시겠습니까?')) {
      localStorage.removeItem(STORAGE_KEY);
      updateStorageInfo();
      setMessage('✅ 모든 파일이 삭제되었습니다.');
      setTimeout(() => setMessage(''), 2000);
      if (onUploadSuccess) onUploadSuccess(null); // 목록 새로고침
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const validation = validateFile(selectedFile);
    
    if (!validation.valid) {
      alert(validation.message);
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
    const sizeMB = (selectedFile.size / 1024 / 1024).toFixed(2);
    setMessage(`✅ 파일 선택됨: ${selectedFile.name} (${sizeMB}MB)`);
  };

  const uploadFile = async () => {
    if (!file) {
      alert('파일을 먼저 선택해주세요!');
      return;
    }

    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    const spaceCheck = checkStorageSpace(file);
    if (!spaceCheck.hasSpace) {
      const shouldDeleteOld = window.confirm(
        `${spaceCheck.message}\n\n가장 오래된 파일을 삭제하고 업로드하시겠습니까?`
      );
      
      if (shouldDeleteOld) {
        const deleted = deleteOldestFile();
        if (!deleted) {
          alert('삭제할 파일이 없습니다.');
          return;
        }
        setMessage('🗑️ 오래된 파일을 삭제했습니다. 업로드를 계속합니다...');
      } else {
        return;
      }
    }

    setUploading(true);
    setMessage('📤 파일 변환 중...');

    try {
      setProgress(10);
      await new Promise(resolve => setTimeout(resolve, 200));

      // 파일을 Base64로 변환
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setProgress(50);
      setMessage('💾 파일 저장 중...');
      await new Promise(resolve => setTimeout(resolve, 300));

      // 파일 데이터 생성
      const fileData = {
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        originalName: file.name,
        title: file.name.replace('.pdf', ''),
        size: file.size,
        formattedSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        uploadDate: new Date().toLocaleDateString('ko-KR'),
        uploadTime: new Date().toLocaleTimeString('ko-KR'),
        uploadedBy: 'user@example.com',
        subject: '국어',
        difficulty: '중',
        downloads: 0,
        createdAt: new Date().toISOString(),
        base64Data: base64Data,
        fileType: file.type,
        isMockData: false
      };

      setProgress(80);
      setMessage('🗃️ 저장소에 저장 중...');

      // 재시도 로직으로 저장
      let saved = false;
      let retries = 0;
      const maxRetries = 3;

      while (!saved && retries < maxRetries) {
        try {
          const existingFiles = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
          existingFiles.unshift(fileData);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(existingFiles));
          saved = true;
        } catch (error) {
          retries++;
          console.warn(`저장 시도 ${retries}/${maxRetries} 실패:`, error);
          
          if (error.name === 'QuotaExceededError') {
            const deleted = deleteOldestFile();
            if (!deleted) {
              throw new Error('저장 공간 부족: 삭제할 파일이 없습니다.');
            }
            setMessage(`🗑️ 공간 확보를 위해 오래된 파일을 삭제했습니다... (${retries}/${maxRetries})`);
          } else {
            throw error;
          }
        }
      }

      if (!saved) {
        throw new Error('저장에 실패했습니다. 파일이 너무 크거나 저장소에 문제가 있습니다.');
      }

      setProgress(100);
      setMessage(`🎉 업로드 완료! "${file.name}" 파일이 성공적으로 저장되었습니다.`);
      
      updateStorageInfo();
      console.log('✅ 파일 저장 완료:', fileData.id);

      if (onUploadSuccess) {
        onUploadSuccess(fileData);
      }

      // 3초 후 상태 초기화
      setTimeout(() => {
        setFile(null);
        setProgress(0);
        setMessage('');
        setUploading(false);
        const fileInput = document.getElementById('optimized-file-input');
        if (fileInput) fileInput.value = '';
      }, 3000);

    } catch (error) {
      console.error('업로드 실패:', error);
      setMessage(`❌ 업로드 실패: ${error.message}`);
      setUploading(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  return (
    <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
      <h3 className="text-lg font-bold text-purple-800 mb-4">📚 최적화된 파일 업로드 시스템</h3>
      
      {/* 저장소 상태 */}
      <div className="mb-4 p-3 bg-white rounded border">
        <h4 className="font-semibold text-gray-700 mb-2">💾 저장소 상태</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>사용량:</span>
            <span className={parseFloat(storageInfo.used) > 4 ? 'text-red-600 font-bold' : 'text-gray-600'}>
              {storageInfo.used}MB / {storageInfo.total}MB
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                parseFloat(storageInfo.used) > 4 ? 'bg-red-500' : 
                parseFloat(storageInfo.used) > 3 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((parseFloat(storageInfo.used) / storageInfo.total) * 100, 100)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-sm">
            <span>저장된 파일: {storageInfo.files}개</span>
            {storageInfo.files > 0 && (
              <Button
                onClick={clearAllFiles}
                variant="link"
                size="sm"
                className="text-red-600 hover:text-red-800 h-auto p-0"
              >
                전체 삭제
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 p-3 bg-white rounded border">
        <p className="text-sm text-gray-600">
          ⚡ <strong>최적화!</strong> 파일 크기 제한({(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB)과 저장소 관리로 안정적인 업로드를 제공합니다.
        </p>
      </div>

      {/* 현재 상태 표시 */}
      <div className="mb-4 p-3 bg-white rounded border">
        <p><strong>📁 선택된 파일:</strong> {file ? `✅ ${file.name}` : '❌ 파일 없음'}</p>
        <p><strong>📊 업로드 상태:</strong> {uploading ? `📤 진행 중 (${progress}%)` : '⏸️ 대기 중'}</p>
        <p><strong>📏 크기 제한:</strong> 최대 {(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB</p>
      </div>

      {/* 파일 선택 */}
      <div className="mb-4">
        <label htmlFor="optimized-file-input" className="block text-sm font-medium text-purple-700 mb-2">
          PDF 파일 선택 (최대 {(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB):
        </label>
        <input
          id="optimized-file-input"
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
          disabled={uploading}
          className="w-full p-2 border border-purple-300 rounded focus:border-purple-500 focus:outline-none"
        />
      </div>

      {/* 진행률 바 */}
      {uploading && (
        <div className="mb-4">
          <div className="w-full bg-purple-200 rounded-full h-4">
            <div
              className="bg-purple-600 h-4 rounded-full transition-all duration-500 flex items-center justify-center"
              style={{ width: `${progress}%` }}
            >
              <span className="text-white text-xs font-bold">{progress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* 메시지 */}
      {message && (
        <div className={`mb-4 p-3 rounded ${
          message.includes('완료') || message.includes('🎉') ? 'bg-green-100 text-green-700 border border-green-300' :
          message.includes('실패') || message.includes('❌') ? 'bg-red-100 text-red-700 border border-red-300' :
          message.includes('삭제') ? 'bg-orange-100 text-orange-700 border border-orange-300' :
          'bg-blue-100 text-blue-700 border border-blue-300'
        }`}>
          <div className="flex items-center gap-2">
            <span>{message}</span>
          </div>
        </div>
      )}

      {/* 🆕 Shadcn Button으로 교체된 업로드 버튼 */}
      <Button
        onClick={uploadFile}
        disabled={!file || uploading || parseFloat(storageInfo.used) > 4.8}
        size="lg"
        className={`w-full ${
          !file || uploading || parseFloat(storageInfo.used) > 4.8
            ? 'opacity-50 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-700 hover:shadow-lg'
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <span>저장 중... {progress}%</span>
          </div>
        ) : parseFloat(storageInfo.used) > 4.8 ? (
          '❌ 저장 공간 부족 - 파일을 삭제하세요'
        ) : (
          '💾 최적화된 업로드 시작'
        )}
      </Button>

      {/* 사용 팁 */}
      <div className="mt-4 p-3 bg-purple-100 rounded-lg">
        <h4 className="text-purple-900 font-medium mb-2">💡 사용 팁</h4>
        <div className="text-sm text-purple-800 space-y-1">
          <p>• 파일 크기는 {(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)}MB 이하로 제한됩니다</p>
          <p>• 저장소가 부족하면 가장 오래된 파일이 자동 삭제됩니다</p>
          <p>• 빨간색 경고가 나타나면 일부 파일을 삭제해주세요</p>
        </div>
      </div>
    </div>
  );
};

export default OptimizedFileUpload;